import { createFileRoute, Link } from "@tanstack/react-router";
import logoAsset from "@/assets/psy-bloom-logo.png.asset.json";
import { useRouteHead } from "@/hooks/use-route-head";

const routeHead = {
  meta: [
    { title: "Politica de Cookies | PSY BLOOM Institute" },
    {
      name: "description",
      content:
        "Politica de cookies a PSY BLOOM Institute. Informații despre utilizarea cookie-urilor și tehnologiilor similare pe website.",
    },
    { property: "og:title", content: "Politica de Cookies | PSY BLOOM Institute" },
    {
      property: "og:description",
      content:
        "Politica de cookies a PSY BLOOM Institute. Informații despre utilizarea cookie-urilor și tehnologiilor similare pe website.",
    },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "/politica-cookies" },
  ],
  links: [{ rel: "canonical", href: "/politica-cookies" }],
};

export const Route = createFileRoute("/politica-cookies")({
  component: PoliticaCookiesPage,
});

function PoliticaCookiesPage() {
  useRouteHead(routeHead);
  return (
    <div className="min-h-screen bg-background">
      {/* HEADER */}
      <header className="w-full border-b border-border/60 bg-background/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" aria-label="PSY BLOOM Institute">
            <img src={logoAsset.url} alt="PSY BLOOM Institute" className="h-9 w-auto" width={180} height={36} />
          </Link>
          <a
            href="https://wa.me/40700000000"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition-all hover:bg-primary/90 hover:shadow-[var(--shadow-card)]"
          >
            Programează o discuție
          </a>
        </div>
      </header>

      <main>
        <section className="py-16 md:py-24">
          <div className="mx-auto max-w-3xl px-6">
            <h1 className="text-4xl md:text-5xl leading-[1.1] mb-6 text-balance">
              Politica de Cookies
            </h1>
            <p className="text-sm text-muted-foreground mb-12">
              Ultima actualizare: iunie 2026
            </p>

            <div className="space-y-12 text-base md:text-lg text-muted-foreground leading-relaxed">
              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  1. Ce este această Politică de Cookies
                </h2>
                <div className="space-y-4">
                  <p>
                    Această Politică de Cookies explică modul în care website-ul PSY BLOOM Institute utilizează cookie-uri și tehnologii similare pentru a asigura funcționarea website-ului, pentru a analiza utilizarea acestuia și pentru a măsura eficiența activităților de promovare online.
                  </p>
                  <p>
                    Prezenta politică trebuie citită împreună cu Politica de Confidențialitate și Termenii și Condițiile website-ului.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  2. Ce sunt cookie-urile
                </h2>
                <div className="space-y-4">
                  <p>
                    Cookie-urile sunt fișiere text de mici dimensiuni stocate pe dispozitivul utilizatorului atunci când acesta vizitează un website.
                  </p>
                  <p>
                    Acestea permit website-ului să recunoască dispozitivul utilizatorului și să rețină anumite informații despre vizită, preferințe sau interacțiuni.
                  </p>
                  <p>
                    Cookie-urile pot fi plasate direct de către website sau de către servicii terțe integrate în website.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  3. Ce tipuri de cookie-uri utilizăm
                </h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">Cookie-uri strict necesare</h3>
                    <div className="space-y-3">
                      <p>
                        Aceste cookie-uri sunt necesare pentru funcționarea corectă a website-ului și nu pot fi dezactivate prin sistemul de consimțământ.
                      </p>
                      <p>
                        Ele pot include:
                      </p>
                      <ul className="list-disc pl-6 space-y-2">
                        <li>cookie-uri de securitate;</li>
                        <li>cookie-uri de funcționare a website-ului;</li>
                        <li>cookie-uri necesare afișării corecte a paginilor.</li>
                      </ul>
                      <p>
                        Aceste cookie-uri nu sunt utilizate în scop publicitar.
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">Cookie-uri de analiză și măsurare</h3>
                    <div className="space-y-3">
                      <p>
                        Aceste cookie-uri ne ajută să înțelegem:
                      </p>
                      <ul className="list-disc pl-6 space-y-2">
                        <li>câți utilizatori vizitează website-ul;</li>
                        <li>ce pagini sunt accesate;</li>
                        <li>cum interacționează utilizatorii cu conținutul;</li>
                        <li>ce tipuri de dispozitive și browsere sunt utilizate.</li>
                      </ul>
                      <p>
                        Scopul acestora este îmbunătățirea experienței utilizatorilor și optimizarea website-ului.
                      </p>
                      <p>
                        Aceste cookie-uri sunt activate numai după exprimarea consimțământului, atunci când legislația aplicabilă impune acest lucru.
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">Cookie-uri de marketing și publicitate</h3>
                    <div className="space-y-3">
                      <p>
                        Website-ul utilizează tehnologii de marketing și măsurare a conversiilor, inclusiv Meta Pixel.
                      </p>
                      <p>
                        Aceste tehnologii permit:
                      </p>
                      <ul className="list-disc pl-6 space-y-2">
                        <li>măsurarea performanței campaniilor publicitare;</li>
                        <li>înțelegerea modului în care utilizatorii ajung pe website;</li>
                        <li>optimizarea reclamelor afișate pe platformele Meta.</li>
                      </ul>
                      <p>
                        Datele colectate prin aceste tehnologii pot fi asociate de către furnizorii respectivi cu informații existente în conturile utilizatorilor.
                      </p>
                      <p>
                        Aceste cookie-uri și tehnologii de urmărire sunt activate doar după exprimarea consimțământului utilizatorului.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  4. Tehnologii similare cookie-urilor
                </h2>
                <div className="space-y-4">
                  <p>
                    Pe lângă cookie-uri, website-ul poate utiliza și alte tehnologii similare, inclusiv:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>pixeli de urmărire;</li>
                    <li>identificatori anonimi;</li>
                    <li>stocare locală în browser;</li>
                    <li>tehnologii de măsurare a conversiilor.</li>
                  </ul>
                  <p>
                    Din punct de vedere al protecției datelor, aceste tehnologii sunt tratate similar cookie-urilor atunci când permit identificarea directă sau indirectă a unui utilizator.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  5. Meta Pixel
                </h2>
                <div className="space-y-4">
                  <p>
                    Website-ul utilizează Meta Pixel, un serviciu furnizat de Meta.
                  </p>
                  <p>
                    Meta Pixel permite măsurarea eficienței campaniilor publicitare și înțelegerea modului în care utilizatorii interacționează cu website-ul.
                  </p>
                  <p>
                    Prin intermediul Meta Pixel pot fi colectate informații precum:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>paginile vizitate;</li>
                    <li>timpul petrecut pe website;</li>
                    <li>anumite interacțiuni cu conținutul;</li>
                    <li>evenimente de conversie.</li>
                  </ul>
                  <p>
                    Meta Pixel este activat numai după exprimarea consimțământului utilizatorului prin bannerul de cookie-uri.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  6. Consimțământul utilizatorului
                </h2>
                <div className="space-y-4">
                  <p>
                    În conformitate cu legislația europeană privind protecția datelor și confidențialitatea comunicațiilor electronice:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>cookie-urile strict necesare pot fi utilizate fără consimțământ;</li>
                    <li>cookie-urile de analiză și marketing necesită consimțământ prealabil;</li>
                    <li>utilizatorul trebuie să poată accepta sau refuza în mod liber aceste categorii de cookie-uri.</li>
                  </ul>
                  <p>
                    Bannerul de cookie-uri afișat pe website permite utilizatorului:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>să accepte toate cookie-urile;</li>
                    <li>să refuze cookie-urile opționale;</li>
                    <li>să își personalizeze preferințele.</li>
                  </ul>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  7. Retragerea consimțământului
                </h2>
                <div className="space-y-4">
                  <p>
                    Utilizatorii își pot modifica sau retrage consimțământul în orice moment.
                  </p>
                  <p>
                    Retragerea consimțământului nu afectează legalitatea prelucrărilor efectuate înainte de retragere.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  8. Cum puteți controla cookie-urile
                </h2>
                <div className="space-y-4">
                  <p>
                    Majoritatea browserelor permit:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>vizualizarea cookie-urilor existente;</li>
                    <li>ștergerea cookie-urilor;</li>
                    <li>blocarea totală sau parțială a cookie-urilor.</li>
                  </ul>
                  <p>
                    Dezactivarea anumitor cookie-uri poate afecta funcționarea unor secțiuni ale website-ului.
                  </p>
                  <p>
                    Informații suplimentare despre gestionarea cookie-urilor pot fi găsite în documentația browserului utilizat.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  9. Furnizori terți
                </h2>
                <div className="space-y-4">
                  <p>
                    Website-ul poate utiliza servicii furnizate de terți, inclusiv:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Meta Platforms Ireland Limited;</li>
                    <li>furnizori de găzduire web;</li>
                    <li>furnizori de servicii tehnice și de securitate.</li>
                  </ul>
                  <p>
                    Politicile de confidențialitate și utilizarea cookie-urilor de către acești furnizori sunt guvernate de propriile documente și politici.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  10. Modificarea Politicii de Cookies
                </h2>
                <div className="space-y-4">
                  <p>
                    Ne rezervăm dreptul de a actualiza această Politică de Cookies pentru a reflecta modificări legislative, tehnologice sau operaționale.
                  </p>
                  <p>
                    Versiunea actualizată va fi publicată pe această pagină, iar data ultimei actualizări va fi modificată corespunzător.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  11. Contact
                </h2>
                <p>
                  Pentru întrebări privind utilizarea cookie-urilor și protecția datelor, ne puteți contacta prin intermediul informațiilor disponibile în pagina Contact a website-ului.
                </p>
              </div>

              <div className="pt-8 border-t border-border">
                <p className="text-base italic text-muted-foreground">
                  PSY BLOOM Institute utilizează cookie-uri și tehnologii similare într-un mod responsabil și transparent, cu respectarea dreptului utilizatorilor la confidențialitate, control asupra datelor și informare clară privind prelucrarea acestora.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="border-t border-border bg-[#32506c]">
        <div className="mx-auto max-w-6xl px-6 py-12">
          <nav aria-label="Navigare subsol" className="flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-white/80 mb-8">
            <Link to="/" className="hover:text-white transition-colors">Acasă</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            <a href="https://blog.psybloominstitute.ro" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Blog</a>
            <Link to="/termeni-si-conditii" className="hover:text-white transition-colors">Termeni și condiții</Link>
            <Link to="/politica-de-confidentialitate" className="hover:text-white transition-colors">Politica de confidentialitate</Link>
            <Link to="/politica-cookies" className="hover:text-white transition-colors">Politica cookies</Link>
          </nav>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-6 border-t border-white/20 text-xs text-white/60">
            <p>© {new Date().getFullYear()} PSY BLOOM Institute</p>
            <p>
              Proiect creat de{" "}
              <a href="https://algoritmii.ro" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                Algoritmii.ro
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
